import {
  differenceInCalendarDays,
  format,
  intlFormatDistance,
  parseISO,
} from 'date-fns';
import type { Order, OrderQuery } from '../../api/order';
import { OrderStatus } from '../enum/orderStatus';
import { ReleaseType } from '../enum/releaseType';
import { VestmentCategory } from '../enum/vestmentCategory';
import { getCategory } from '../enum/vestmentType';

export const OPEN_ORDER_STATUSES: OrderStatus[] = [
  OrderStatus.Draft,
  OrderStatus.ReadyForProduction,
  OrderStatus.InProduction,
  OrderStatus.ReadyForRelease,
];

export const CLOSED_ORDER_STATUSES: OrderStatus[] = [
  OrderStatus.Released,
  OrderStatus.Cancelled,
];

export type OrderStatusFilter = OrderStatus | 'open' | 'closed' | '';

export interface OrderFilters {
  status: OrderStatusFilter;
  category: VestmentCategory | '';
}

export const EMPTY_ORDER_FILTERS: OrderFilters = {
  status: '',
  category: '',
};

export function orderFiltersToQueryParams(
  searchTerm: string,
  filters: OrderFilters
): OrderQuery {
  const query: OrderQuery = {};
  const search = searchTerm.trim();
  if (search) {
    query.search = search;
  }
  if (filters.status) {
    query.status = filters.status;
  }
  if (filters.category) {
    query.category = filters.category;
  }
  return query;
}

export function countActiveOrderFilters(filters: OrderFilters): number {
  let count = 0;
  if (filters.status) count += 1;
  if (filters.category) count += 1;
  return count;
}

export function matchOrderFilters(
  order: Order,
  filters: OrderFilters
): boolean {
  if (filters.status === 'open') {
    if (!OPEN_ORDER_STATUSES.includes(order.status)) {
      return false;
    }
  } else if (filters.status === 'closed') {
    if (!CLOSED_ORDER_STATUSES.includes(order.status)) {
      return false;
    }
  } else if (filters.status && order.status !== filters.status) {
    return false;
  }
  if (filters.category) {
    const hasMatchingItem = order.items.some((item) => {
      const type = item.vestment?.type;
      return type && getCategory(type) === filters.category;
    });
    if (!hasMatchingItem) {
      return false;
    }
  }
  return true;
}

export function canEditOrder(status: OrderStatus): boolean {
  return ![OrderStatus.Released, OrderStatus.Cancelled].includes(status);
}

export function canUndoReleaseOrder(status: OrderStatus): boolean {
  return status === OrderStatus.Released;
}

export function canCancelOrder(status: OrderStatus): boolean {
  return [OrderStatus.InProduction, OrderStatus.ReadyForRelease].includes(
    status
  );
}

export function canUndoCancelOrder(status: OrderStatus): boolean {
  return status === OrderStatus.Cancelled;
}

export function canDeleteOrder(status: OrderStatus): boolean {
  return [
    OrderStatus.Draft,
    OrderStatus.ReadyForProduction,
    OrderStatus.Cancelled,
  ].includes(status);
}

export function isActiveProdOrder(status: OrderStatus): boolean {
  return [OrderStatus.InProduction, OrderStatus.ReadyForRelease].includes(
    status
  );
}

export function isDueDateApproaching(
  dueDate: string,
  status?: OrderStatus
): boolean {
  if (status === OrderStatus.Released || status === OrderStatus.Cancelled) {
    return false;
  }
  return differenceInCalendarDays(parseISO(dueDate), new Date()) <= 7;
}

export function formatDueDate(dueDate: string, status?: OrderStatus): string {
  const date = parseISO(dueDate);
  if (isDueDateApproaching(dueDate, status)) {
    return intlFormatDistance(date, new Date(), { unit: 'day' }).replace(
      /^./,
      (c) => c.toUpperCase()
    );
  }
  return format(date, 'MMM d, yyyy');
}

function countLabel(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

export function getOrderProgressPercent(
  order: Pick<Order, 'status' | 'items'>
): number {
  switch (order.status) {
    case OrderStatus.Draft:
    case OrderStatus.ReadyForProduction:
    case OrderStatus.Cancelled:
      return 0;
    case OrderStatus.ReadyForRelease:
    case OrderStatus.Released:
      return 100;
    case OrderStatus.InProduction: {
      let weighted = 0;
      let quantity = 0;
      for (const item of order.items) {
        const count = item.quantity ?? 1;
        weighted += (item.productionProgress ?? 0) * count;
        quantity += count;
      }
      if (quantity <= 0) return 0;
      return Math.round(weighted / quantity);
    }
    default: {
      const _exhaustive: never = order.status;
      return _exhaustive;
    }
  }
}

export interface OrderTimingLine {
  text: string;
  color: 'text.secondary' | 'red.700' | 'green.800';
}

function releasePerformanceVerb(releaseType?: ReleaseType | null): string {
  if (!releaseType) return 'Completed';
  switch (releaseType) {
    case ReleaseType.Pickup:
      return 'Picked up';
    case ReleaseType.Delivery:
      return 'Delivered';
    case ReleaseType.Shipped:
      return 'Shipped';
    default: {
      const _exhaustive: never = releaseType;
      return _exhaustive;
    }
  }
}

function formatFulfillmentLine(
  dueDate: string,
  releasedAt: string,
  releaseType?: ReleaseType | null
): OrderTimingLine {
  const days = differenceInCalendarDays(
    parseISO(releasedAt),
    parseISO(dueDate)
  );
  const verb = releasePerformanceVerb(releaseType);
  if (days === 0) return { text: `${verb} on time`, color: 'green.800' };
  const early = days < 0;
  return {
    text: `${verb} ${countLabel(Math.abs(days), 'day')} ${early ? 'early' : 'late'}`,
    color: early ? 'green.800' : 'red.700',
  };
}

function formatOpenDueLine(dueDate: string, now: Date): OrderTimingLine {
  const days = differenceInCalendarDays(parseISO(dueDate), now);
  if (days > 1) return { text: `In ${days} days`, color: 'text.secondary' };
  if (days === 1) return { text: 'Due tomorrow', color: 'text.secondary' };
  if (days === 0) return { text: 'Due today', color: 'text.secondary' };
  if (days === -1) return { text: 'Due yesterday', color: 'red.700' };
  return {
    text: `${countLabel(Math.abs(days), 'day')} overdue`,
    color: 'red.700',
  };
}

export function formatOrderListDueDate(order: Pick<Order, 'dueDate'>): string {
  if (order.dueDate) return format(parseISO(order.dueDate), 'MMM d, yyyy');
  return 'No due date';
}

export function getOrderTimingLine(
  order: Pick<
    Order,
    'status' | 'dueDate' | 'releasedAt' | 'releaseType' | 'createdAt'
  >,
  now: Date = new Date()
): OrderTimingLine | null {
  switch (order.status) {
    case OrderStatus.Cancelled:
      return null;
    case OrderStatus.Released: {
      if (!order.releasedAt) return null;
      if (!order.dueDate) {
        const verb = releasePerformanceVerb(order.releaseType);
        return {
          text: `${verb} on ${format(parseISO(order.releasedAt), 'MMM d, yyyy')}`,
          color: 'text.secondary',
        };
      }
      return formatFulfillmentLine(
        order.dueDate,
        order.releasedAt,
        order.releaseType
      );
    }
    case OrderStatus.Draft:
    case OrderStatus.ReadyForProduction:
    case OrderStatus.InProduction:
    case OrderStatus.ReadyForRelease: {
      if (!order.dueDate) {
        return {
          text: `Created on ${format(parseISO(order.createdAt), 'MMM d, yyyy')}`,
          color: 'text.secondary',
        };
      }
      return formatOpenDueLine(order.dueDate, now);
    }
    default: {
      const _exhaustive: never = order.status;
      return _exhaustive;
    }
  }
}
